import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { Input } from '../../common/components/inputs';
import { Button } from '../../common/components/buttons';
import { ErrorMessage } from '../../common/components/feedback';
import { AuthCard, AuthLayout } from '../ui';
import { useFormValidation } from '../hooks';
import { login } from '../../../services/authService';

/**
 * LoginPage - Página de login de usuários
 */
export const LoginPage = () => {
  const navigate = useNavigate();
  const { validateEmail, validateLoginPassword } = useFormValidation();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setGeneralError('');
    setErrors({});

    // Validação
    const validationErrors: Record<string, string> = {};

    const emailError = validateEmail(email);
    if (emailError) validationErrors.email = emailError;

    const passwordError = validateLoginPassword(password);
    if (passwordError) validationErrors.password = passwordError;

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Submit
    setIsLoading(true);
    try {
      const response = await login(email, password);
      if (response && response.access_token) {
        window.dispatchEvent(new Event('storage'));

        // Check for pending invite token and redirect accordingly
        const pendingInviteToken = localStorage.getItem('pendingInviteToken');
        if (pendingInviteToken) {
          navigate('/accept-invite');
        } else {
          navigate('/chat');
        }
      } else {
        setGeneralError('Erro de autenticação: tokens não recebidos');
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Email não verificado') {
        localStorage.setItem('pendingVerificationEmail', email);
        navigate('/verify-email', { state: { email } });
      } else {
        setGeneralError(err instanceof Error ? err.message : 'Erro de autenticação');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthCard title="Bem-vindo de volta" subtitle="Entre com sua conta para continuar">
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            icon={Mail}
            required
          />

          <div>
            <Input
              label="Senha"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
              icon={Lock}
              showPasswordToggle
              required
            />
            <div className="mt-2 text-right">
              <button
                type="button"
                onClick={() => navigate('/esqueci-senha')}
                className="text-sm text-[#F4CC04] hover:underline"
              >
                Esqueceu sua senha?
              </button>
            </div>
          </div>

          <ErrorMessage>{generalError}</ErrorMessage>

          <Button type="submit" variant="primary" fullWidth loading={isLoading}>
            Entrar
          </Button>

          <p className="text-center text-[#CCCCCC] text-sm">
            Não tem uma conta?{' '}
            <button
              type="button"
              onClick={() => navigate('/cadastro')}
              className="text-[#F4CC04] hover:underline font-medium"
            >
              Cadastre-se
            </button>
          </p>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};
